<script setup lang="ts">
import { ArrowUpRight, CornerDownLeft, Search, X } from '@lucide/vue'
import type { SpotlightResult } from '~~/shared/types/spotlight'
import { spotlightIconComponents } from '~/utils/spotlightIcons'
import {
  isApplePlatform,
  matchesSpotlightShortcut,
  spotlightShortcutHint,
} from '~~/shared/utils/spotlight'

const spotlight = useSpotlight()
const analytics = useAnalytics()
const input = ref<HTMLInputElement>()
const selectedIndex = ref(-1)
const apple = ref(false)
const shortcutHint = computed(() => spotlightShortcutHint('mod-k', apple.value))
const activeDescendant = computed(() =>
  selectedIndex.value >= 0 ? `global-spotlight-result-${selectedIndex.value}` : undefined,
)

function usesApplicationArtwork(result: SpotlightResult) {
  return (
    Boolean(result.customIcon) ||
    (result.provider === 'applications' && result.entityType === 'application')
  )
}

function openSpotlight() {
  spotlight.open.value = true
  selectedIndex.value = -1
  document.body.classList.add('spotlight-open')
  analytics.trackEvent('search_open', { surface: 'spotlight', source: 'shortcut', query_length: 0 })
  nextTick(() => input.value?.focus())
}

function closeSpotlight() {
  spotlight.open.value = false
  document.body.classList.remove('spotlight-open')
  input.value?.blur()
}

function selectResult(result: SpotlightResult, forceNewTab = false) {
  const position = spotlight.results.value.findIndex((item) => item.id === result.id)
  const newTab = forceNewTab || result.target.newTab
  analytics.trackEvent('search_select', {
    surface: 'spotlight',
    query_length: spotlight.query.value.length,
    result_count: spotlight.results.value.length,
    result_type: result.provider,
    position: position + 1,
    open_mode: newTab ? 'new_tab' : 'same_tab',
  })
  void morningFetch('/api/spotlight/selection', {
    method: 'POST',
    body: { provider: result.provider, entityId: result.entityId },
  }).catch(() => {})
  const url = result.target.url
  const internal = url.startsWith('/') && !url.startsWith('//')
  if (!internal && !/^https?:\/\//i.test(url)) return
  closeSpotlight()
  if (newTab) window.open(url, '_blank', 'noopener,noreferrer')
  else if (internal) {
    analytics.setNavigationSource('spotlight')
    void navigateTo(url)
  } else window.location.assign(url)
}

function submit(event: KeyboardEvent) {
  const items = spotlight.results.value
  const result = items.length === 1 ? items[0] : items[selectedIndex.value]
  if (result) selectResult(result, event.altKey)
}

function move(direction: 1 | -1) {
  const count = spotlight.results.value.length
  if (!count) return
  selectedIndex.value = (selectedIndex.value + direction + count) % count
  nextTick(() =>
    document
      .getElementById(`global-spotlight-result-${selectedIndex.value}`)
      ?.scrollIntoView({ block: 'nearest' }),
  )
}

function onKeydown(event: KeyboardEvent) {
  if (!spotlight.open.value && matchesSpotlightShortcut(event, 'mod-k', apple.value)) {
    event.preventDefault()
    openSpotlight()
  } else if (spotlight.open.value && event.key === 'Escape') {
    event.preventDefault()
    if (spotlight.query.value) spotlight.query.value = ''
    else closeSpotlight()
  }
}

watch(spotlight.results, () => {
  selectedIndex.value = spotlight.results.value.length ? 0 : -1
})
onMounted(() => {
  apple.value = isApplePlatform(navigator.platform, navigator.userAgent)
  window.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  document.body.classList.remove('spotlight-open')
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="spotlight.open.value"
      class="search-spotlight active analytics-sensitive"
      role="dialog"
      aria-modal="true"
      aria-label="Recherche rapide"
      @pointerdown.self="closeSpotlight"
    >
      <div class="search-area">
        <label class="application-search">
          <Search
            :size="20"
            :stroke-width="2"
            aria-hidden="true"
          />
          <span class="sr-only">Rechercher dans Morning</span>
          <input
            ref="input"
            v-model="spotlight.query.value"
            type="search"
            placeholder="Rechercher dans Morning…"
            autocomplete="off"
            spellcheck="false"
            role="combobox"
            aria-expanded="true"
            aria-controls="global-spotlight-results"
            :aria-activedescendant="activeDescendant"
            aria-autocomplete="list"
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.enter.prevent="submit"
          />
          <button
            v-if="spotlight.query.value"
            type="button"
            class="search-clear"
            aria-label="Effacer la recherche"
            @click="spotlight.query.value = ''"
          >
            <X :size="17" />
          </button>
          <kbd v-else-if="shortcutHint">{{ shortcutHint }}</kbd>
        </label>
        <div
          id="global-spotlight-results"
          class="spotlight-results analytics-sensitive"
          role="listbox"
          aria-label="Résultats de recherche"
        >
          <div class="spotlight-results-heading">
            <span>{{ spotlight.query.value ? 'Résultats' : 'Récents' }}</span>
            <span>{{
              spotlight.loading.value ? 'Recherche…' : spotlight.results.value.length
            }}</span>
          </div>
          <div
            v-if="spotlight.results.value.length"
            class="spotlight-results-list"
          >
            <a
              v-for="(result, index) in spotlight.results.value"
              :id="`global-spotlight-result-${index}`"
              :key="result.id"
              class="spotlight-result analytics-sensitive"
              :class="{ selected: selectedIndex === index }"
              :href="result.target.url"
              role="option"
              :aria-selected="selectedIndex === index"
              @pointerenter="selectedIndex = index"
              @focus="selectedIndex = index"
              @click.prevent="selectResult(result)"
            >
              <DashboardApplicationIcon
                v-if="usesApplicationArtwork(result)"
                :application="{ name: result.title, icon: result.customIcon }"
              />
              <span
                v-else
                class="spotlight-result-icon"
                :class="result.icon ? `icon-${result.icon}` : undefined"
                aria-hidden="true"
              >
                <component
                  :is="spotlightIconComponents[result.icon]"
                  v-if="result.icon"
                  :size="20"
                  :stroke-width="1.8"
                />
                <Search
                  v-else
                  :size="18"
                />
              </span>
              <span class="spotlight-result-copy">
                <strong>{{ result.title }}</strong>
                <span>{{ result.subtitle }}</span>
                <small>
                  <span
                    v-if="result.badge"
                    class="spotlight-result-badge"
                    >{{ result.badge }}</span
                  >
                  <span
                    v-if="result.archived"
                    class="spotlight-result-state"
                    >Archivé</span
                  >
                  <span
                    v-if="result.completed"
                    class="spotlight-result-state"
                    >Terminé</span
                  >
                </small>
              </span>
              <span
                class="spotlight-result-action"
                aria-hidden="true"
                ><CornerDownLeft
                  v-if="selectedIndex === index"
                  :size="16" /><ArrowUpRight
                  v-else
                  :size="16"
              /></span>
            </a>
          </div>
          <div
            v-else-if="spotlight.error.value"
            class="spotlight-no-result"
          >
            Recherche temporairement indisponible.
          </div>
          <div
            v-else-if="spotlight.query.value && !spotlight.loading.value"
            class="spotlight-no-result"
          >
            Aucun résultat.
          </div>
          <div
            v-else-if="!spotlight.query.value"
            class="spotlight-no-result"
          >
            Saisissez votre recherche.
          </div>
          <div class="spotlight-results-footer">
            <span>Entrée ouvre le résultat sélectionné</span>
            <span
              class="spotlight-keyboard-hints"
              aria-hidden="true"
              ><span class="spotlight-keyboard-hint"><kbd>↑</kbd><kbd>↓</kbd> sélectionner</span
              ><span class="spotlight-keyboard-hint"
                ><kbd>{{ apple ? '⌥' : 'Alt' }}</kbd
                ><kbd>↵</kbd> nouvel onglet</span
              ></span
            >
          </div>
        </div>
        <p
          class="search-feedback"
          aria-live="polite"
        >
          {{ spotlight.error.value ? 'Recherche indisponible' : '' }}
        </p>
      </div>
    </div>
  </Teleport>
</template>
